import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Award, ShieldAlert, ThumbsUp, ThumbsDown, Ban, Sparkles, ArrowLeft, Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";
import Header from "../components/Header";
import LinkEmailCard from "../components/LinkEmailCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  trusted: { ring: "stroke-green-500", text: "text-green-700", bg: "bg-green-50", chip: "bg-green-100 text-green-800" },
  neutral: { ring: "stroke-gray-700", text: "text-gray-900", bg: "bg-gray-50", chip: "bg-gray-200 text-gray-800" },
  low: { ring: "stroke-red-500", text: "text-red-700", bg: "bg-red-50", chip: "bg-red-100 text-red-800" },
};

/**
 * Circular trust meter — pure SVG so it scales crisply on every density and
 * doesn't pull a chart library for one gauge.
 */
function TrustMeter({ score, tier, label }) {
  const styles = TIER_STYLES[tier] || TIER_STYLES.neutral;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);
  return (
    <div className="flex flex-col items-center" data-testid="trust-meter">
      <div className="relative" style={{ width: 140, height: 140 }}>
        <svg width={140} height={140} viewBox="0 0 140 140" className="-rotate-90">
          <circle cx="70" cy="70" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="10" />
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            className={styles.ring}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 600ms ease-out" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className={`font-heading font-black text-4xl tracking-tighter leading-none ${styles.text}`} data-testid="trust-score-value">{score}</p>
          <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mt-1">/ 100</p>
        </div>
      </div>
      <span className={`mt-4 text-[10px] uppercase tracking-widest font-bold rounded-sm px-2 py-1 ${styles.chip}`} data-testid="trust-tier-chip">
        {label}
      </span>
    </div>
  );
}

function StatRow({ icon: Icon, label, value, color = "text-gray-900" }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-700 font-body">{label}</span>
      </div>
      <span className={`text-base font-heading font-black ${color}`}>{value}</span>
    </div>
  );
}

export default function MyTrustPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [t, n] = await Promise.all([
          axios.get(`${API}/users/me/trust`, { withCredentials: true }),
          axios.get(`${API}/notifications`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setData(t.data);
        setNotifications(n.data || []);
      } catch (err) {
        logging.error("My Trust", err);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data || []);
    } catch (err) { logging.error("notifications", err); }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="my-trust-page">
      <Header notifications={notifications} onNotificationRead={fetchNotifications} />

      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 font-body mb-3 transition" data-testid="back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("trust.back")}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <Award className="w-7 h-7 text-gray-900" strokeWidth={2} />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">{t("trust.title")}</h1>
            <p className="text-sm text-gray-500 font-body">{t("trust.subtitle")}</p>
          </div>
        </div>

        <LinkEmailCard />

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-sm p-12 flex items-center justify-center text-sm text-gray-400 font-body">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("trust.loading")}
          </div>
        ) : !data ? (
          <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-400 font-body">
            {t("trust.failed")}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {/* Score gauge */}
            <div className="md:col-span-1 bg-white border border-gray-200 rounded-sm p-6 flex flex-col items-center">
              <TrustMeter score={data.trust_score} tier={data.tier} label={data.tier_label} />
              {data.sanctioned && (
                <div className="mt-4 w-full bg-red-50 border border-red-200 rounded-sm p-3 flex gap-2" data-testid="trust-sanction-banner">
                  <Ban className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-red-800 font-body leading-snug">
                    {t("trust.sanction_banner")}
                  </p>
                </div>
              )}
              <p className="mt-4 text-[10px] text-center text-gray-400 font-body leading-snug">
                {t("trust.bounded")}
              </p>
            </div>

            {/* Breakdown + how it moves */}
            <div className="md:col-span-2 space-y-4">
              <div className="bg-white border border-gray-200 rounded-sm p-5">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">{t("trust.track_record")}</p>
                <StatRow icon={ShieldAlert} label={t("trust.total_alerts")} value={data.breakdown.total_alerts} />
                <StatRow icon={CheckCircle} label={t("trust.resolved_alerts")} value={data.breakdown.resolved_alerts} color="text-green-700" />
                <StatRow icon={ThumbsUp} label={t("trust.community_trusted")} value={data.breakdown.community_trusted} color="text-green-700" />
                <StatRow icon={Sparkles} label={t("trust.admin_verified")} value={data.breakdown.admin_verified} color="text-blue-700" />
                <StatRow icon={ThumbsDown} label={t("trust.flagged_false")} value={data.breakdown.flagged_false} color={data.breakdown.flagged_false > 0 ? "text-red-700" : "text-gray-900"} />
                <StatRow icon={Ban} label={t("trust.sanctions_received")} value={data.breakdown.sanctions_received} color={data.breakdown.sanctions_received > 0 ? "text-red-700" : "text-gray-900"} />
              </div>

              <div className="bg-white border border-gray-200 rounded-sm p-5">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">{t("trust.how_moves")}</p>
                <ul className="space-y-2" data-testid="trust-rules">
                  {data.rules.map((r) => (
                    <li key={r.event} className="flex items-center justify-between text-sm font-body">
                      <span className="text-gray-700">{r.event}</span>
                      <span className={`font-mono font-bold rounded-sm px-1.5 py-0.5 text-xs ${r.kind === "positive" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {r.delta > 0 ? `+${r.delta}` : r.delta}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {data.next_steps?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-5" data-testid="trust-next-steps">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> {t("trust.next_steps")}
                  </p>
                  <ul className="space-y-2 text-sm text-amber-900 font-body list-disc list-inside">
                    {data.next_steps.map((s) => <li key={s}>{s}</li>)}
                  </ul>
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-sm p-5">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">{t("trust.tips_title")}</p>
                <ul className="space-y-2 text-sm text-gray-700 font-body list-disc list-inside" data-testid="trust-tips">
                  {data.tips.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
